import type { CustomFieldDefinitionId } from "@repo/app-types/entities/customField";
import type { TeamPerformancePageFilter } from "@repo/app-types/entities/filters";
import type {
  PlaybookId,
  PlaybookNodeId,
} from "@repo/app-types/entities/playbook";
import type {
  ReportCardData,
  ReportCardId,
  ReportLayout,
  ReportStreamEvent,
} from "@repo/app-types/entities/report";
import type {
  MetadataFieldId,
  SignalId,
} from "@repo/app-types/entities/signal";
import type { DateTime, Zone } from "luxon";
import { api } from "../redux.js";
import { streamQuery } from "../streamQuery.js";

export interface ReportDataStreamState {
  layout: ReportLayout | null;
  data: Record<ReportCardId, ReportCardData | undefined>;
}

export interface ComplianceReportDataStreamArgs {
  type: "compliance";
  periodLengthDays: number;
  periodEnd: DateTime<true>;
  timeZone: Zone<true>;
  playbookId: PlaybookId;
  playbookNodeIds?: readonly PlaybookNodeId[];
  customFieldDefinitionId?: CustomFieldDefinitionId;
  filters?: readonly TeamPerformancePageFilter[];
}

export interface InsuranceUpsellOutcomeReportDataStreamArgs {
  type: "insurance-upsell-outcome";
  periodLengthDays: number;
  periodEnd: DateTime<true>;
  timeZone: Zone<true>;
  signalId?: SignalId;
  initialAmountFieldId?: MetadataFieldId;
  initialTermFieldId?: MetadataFieldId;
  recommendedAmountFieldId?: MetadataFieldId;
  recommendedTermFieldId?: MetadataFieldId;
  coverageRequestCustomFieldDefinitionId?: CustomFieldDefinitionId;
  revenueCustomFieldDefinitionId?: CustomFieldDefinitionId;
  pivotCustomFieldDefinitionId?: CustomFieldDefinitionId;
  filters?: readonly TeamPerformancePageFilter[];
}

export interface PerformanceReportDataStreamArgs {
  type: "performance";
  periodLengthDays: number;
  periodEnd: DateTime<true>;
  timeZone: Zone<true>;
  playbookId: PlaybookId;
  playbookNodeIds?: readonly PlaybookNodeId[];
  filters?: readonly TeamPerformancePageFilter[];
}

type ReportDataStreamArgs =
  | ComplianceReportDataStreamArgs
  | InsuranceUpsellOutcomeReportDataStreamArgs
  | PerformanceReportDataStreamArgs;

const performanceReportApi = api.injectEndpoints({
  endpoints: (builder) => ({
    reportDataStream: streamQuery<ReportDataStreamState, ReportDataStreamArgs>(
      builder,
      {
        paramsToPath: () => `/performance/reports/ephemeral/data-stream`,
        paramsToBody: (args) => {
          switch (args.type) {
            case "insurance-upsell-outcome":
              return {
                type: "insurance-upsell-outcome",
                periodLengthDays: args.periodLengthDays,
                periodEnd: args.periodEnd.toISO(),
                timeZone: args.timeZone.name,
                signalId: args.signalId,
                initialAmountFieldId: args.initialAmountFieldId,
                initialTermFieldId: args.initialTermFieldId,
                recommendedAmountFieldId: args.recommendedAmountFieldId,
                recommendedTermFieldId: args.recommendedTermFieldId,
                coverageRequestCustomFieldDefinitionId:
                  args.coverageRequestCustomFieldDefinitionId,
                revenueCustomFieldDefinitionId:
                  args.revenueCustomFieldDefinitionId,
                pivotCustomFieldDefinitionId: args.pivotCustomFieldDefinitionId,
                filters: args.filters,
              };

            case "compliance":
              return {
                type: "compliance",
                periodLengthDays: args.periodLengthDays,
                periodEnd: args.periodEnd.toISO(),
                timeZone: args.timeZone.name,
                playbookId: args.playbookId,
                playbookNodeIds: args.playbookNodeIds,
                customFieldDefinitionId: args.customFieldDefinitionId,
                filters: args.filters,
              };

            case "performance":
              return {
                type: "performance",
                periodLengthDays: args.periodLengthDays,
                periodEnd: args.periodEnd.toISO(),
                timeZone: args.timeZone.name,
                playbookId: args.playbookId,
                playbookNodeIds: args.playbookNodeIds,
                filters: args.filters,
              };
          }
        },
        initialState: { layout: null, data: {} },
        updateState(state, chunk) {
          const event = chunk as ReportStreamEvent;
          if (event.type === "layout") {
            return { ...state, layout: event.layout };
          }
          if (event.type === "cardData") {
            return {
              ...state,
              data: {
                ...state.data,
                [event.cardId]: event.data,
              },
            };
          }
          return state;
        },
      },
    ),
  }),
});

export const { useReportDataStreamQuery } = performanceReportApi;
